import { getRefundData, getRefundTooltip } from "@/service/meds/refund-service";

import { cn } from "@/lib/utils";
import styles from "./_med-group-card.module.scss";
import { createStyleResolver } from "@/service/class-naming";

const sr = createStyleResolver(styles);

// ─── Standard refund columns (100%, 50%, 30%, R) ─────────────────────────────

export type StandardRefundName = "100%" | "50%" | "30%" | "R";

export function getPriceForRefund(
  prices: MedPriceVM[],
  refund: StandardRefundName,
  link: string,
) {
  const data = getRefundData(prices, refund);
  if (!data) return "-";

  const tooltip = getRefundTooltip(
    prices.filter((p) => p.name === refund),
    link,
  );

  return (
    <span
      className={cn("whitespace-nowrap", tooltip && sr("td__refund-info"))}
      {...(tooltip
        ? {
            "data-title": `<div class="${sr(
              "refund-tooltip",
            )}">${tooltip}</div>`,
            "data-interactive": true,
          }
        : {})}
    >
      {data.priceLabel}
    </span>
  );
}

// ─── Merge price-less variants differing only by packaging ───────────────────

export interface MedVersionRow {
  variant: MedVersionPreviewVM;
  boxes: string[];
}

/**
 * Variants without any price/refundation data that are otherwise identical
 * (name, form, dose, …) are collapsed into a single row listing every
 * packaging (`box`) — avoids rendering many rows differing only by opakowanie.
 * Variants with prices always keep their own row.
 */
export function mergeVersionsByBox(
  versions: MedVersionPreviewVM[],
): MedVersionRow[] {
  const rows: MedVersionRow[] = [];
  const indexByKey = new Map<string, number>();

  for (const variant of versions) {
    if (variant.prices.length > 0) {
      rows.push({ variant, boxes: [variant.box] });
      continue;
    }

    const key = [
      variant.name,
      variant.kind,
      variant.dose,
      variant.chplLink,
      variant.disabled,
    ].join("|");

    const existing = indexByKey.get(key);
    if (existing !== undefined) {
      rows[existing].boxes.push(variant.box);
    } else {
      indexByKey.set(key, rows.length);
      rows.push({ variant, boxes: [variant.box] });
    }
  }

  return rows;
}

// ─── "Other" refund badges (B, S, DZ, C) ─────────────────────────────────────

export type OtherRefundName = "B" | "S" | "DZ" | "C";

const OTHER_REFUND_NAMES: OtherRefundName[] = ["B", "S", "DZ", "C"];

export function getPresentOtherRefunds(prices: MedPriceVM[]) {
  return OTHER_REFUND_NAMES.filter((name) => getRefundData(prices, name));
}

export function getPriceForRefundOther(
  prices: MedPriceVM[],
  refund: OtherRefundName,
  link: string,
) {
  const data = getRefundData(prices, refund);

  if (!data) {
    return (
      <span
        className={cn(
          "flex h-5 w-5 items-center justify-center rounded bg-transparent px-1 py-0.5 text-center text-[13px] leading-none font-normal text-black",
        )}
      >
        -
      </span>
    );
  }

  const tooltip = getRefundTooltip(
    prices.filter((p) => p.name === refund),
    link,
  );

  return (
    <span
      {...(tooltip
        ? {
            "data-title": `<div class="${sr(
              "refund-tooltip",
            )}">${tooltip}</div>`,
            className:
              "flex h-5 w-5 items-center justify-center rounded bg-[#E9F9FB] px-1 py-0.5 text-center text-[13px] leading-none font-normal text-[#058A9D]",
            "data-interactive": true,
          }
        : {
            className:
              "flex h-5 w-5 items-center justify-center rounded bg-[#E9F9FB] px-1 py-0.5 text-center text-[13px] leading-none font-normal text-[#058A9D]",
          })}
    >
      {refund}
    </span>
  );
}

// ─── Availability in pharmacies ──────────────────────────────────────────────

export function getMedGroupSubgroups({
  superGroup,
  versionSubgroups,
}: MedGroupPreviewVM) {
  return superGroup?.versionSubgroups || versionSubgroups;
}

export function isAvailableForSale(version: MedVersionPreviewVM) {
  return (version.salesAvailability ?? "available") === "available";
}

function filterSubgroupVersions(
  subgroups: MedVersionSubgroupVM[],
  keep: (version: MedVersionPreviewVM) => boolean,
): MedVersionSubgroupVM[] {
  return subgroups
    .map((subgroup) => ({
      ...subgroup,
      versions: subgroup.versions.filter(keep),
    }))
    .filter((subgroup) => subgroup.versions.length > 0);
}

export function splitSubgroupsBySalesAvailability(
  subgroups: MedVersionSubgroupVM[],
) {
  return {
    all: subgroups,
    forSale: filterSubgroupVersions(subgroups, isAvailableForSale),
    notForSale: filterSubgroupVersions(
      subgroups,
      (version) => !isAvailableForSale(version),
    ),
  };
}
